<template>
  <div v-if="canRead">
    <div v-for="group in groups" :key="group.key" v-can:read-institutions="{ id: institutionId, keys: [group.institutionKey] }" class="fld">
      <span class="fld__icon"><v-icon>{{ group.icon }}</v-icon></span>
      <div>
        <div class="fld__label">{{ group.label }}</div>
        <div v-if="loading" class="fld__value fld__value--muted">lädt …</div>
        <div v-else-if="!group.items.length" class="fld__value fld__value--muted">{{ group.empty }}</div>
        <div v-else class="chips">
          <v-menu v-for="item in group.items" :key="String(item._id)" offset-y>
            <template v-slot:activator="{ on }">
              <button class="chip" type="button" v-on="on">
                <span class="mini-avatar">{{ initials(item) }}</span>{{ name(item) }}
              </button>
            </template>
            <v-list class="soft-menu py-1" dense>
              <v-list-item @click="show(item._id)">
                <v-list-item-icon><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
                <v-list-item-title>Lehrkraft ansehen</v-list-item-title>
              </v-list-item>
              <v-list-item :href="$router.resolve(`/employees/${item._id}`).href" target="_blank">
                <v-list-item-icon><v-icon small>mdi-open-in-new</v-icon></v-list-item-icon>
                <v-list-item-title>In neuem Tab öffnen</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
      <div class="fld__actions">
        <button v-if="group.canEdit" :class="{ 'ibtn--add': !group.items.length }" v-tip="group.editTitle" :aria-label="group.editTitle" class="ibtn" type="button"
                @click="group.open()">
          <v-icon>{{ group.items.length ? 'mdi-pencil-outline' : 'mdi-plus' }}</v-icon>
        </button>
      </div>
    </div>
    <assign-employees-dialog v-model="showAssign" :assigned-employees="assigned" :institution-id="institutionId"/>
    <plan-employees-dialog v-model="showPlan" :institution-id="institutionId" :planned-employees="planned"/>
    <employee-card-dialog v-if="activeId" v-model="showCard" :employee-id="activeId"/>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import mongoose from 'mongoose';
import AssignEmployeesDialog from '@/components/institutions/institutionView/profileView/assignEmployeesDialog.vue';
import PlanEmployeesDialog from '@/components/institutions/institutionView/profileView/planEmployeesDialog.vue';
import EmployeeCardDialog from '@/components/common/profile/employeeCardDialog.vue';
import {EmployeeHandlerAPI} from '@/classes/api/employee-handler.api.class';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {EmployeeFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/EmployeeFields';
import {Employee} from '@/interfaces/employee.interface';
import {EventBus} from '@/busses/EventBus';

type Person = Pick<Employee, '_id' | 'firstName' | 'lastName'>;

/** Assigned and planned teachers of a school, edited with the same dialogs as the profile */
export default Vue.extend({
  components: {AssignEmployeesDialog, PlanEmployeesDialog, EmployeeCardDialog},
  props: {
    institutionId: {type: Object as () => mongoose.Types.ObjectId, required: true},
  },
  data: () => ({
    loading: false,
    assigned: [] as Person[],
    planned: [] as Person[],
    activeId: null as mongoose.Types.ObjectId | null,
    showCard: false,
    showAssign: false,
    showPlan: false,
  }),
  computed: {
    canRead(): boolean {
      return this.$$crudAccessManager.canReadAllProvided(CrudEntityTypes.EMPLOYEE, EmployeeFields.ASSIGNED_INSTITUTIONS);
    },
    groups(): Array<{ key: string; label: string; icon: string; empty: string; editTitle: string; institutionKey: string; items: Person[];
      canEdit: boolean; open: () => void }> {
      const canUpdate = (field: string) => this.$$crudAccessManager.canReadAllProvided(CrudEntityTypes.EMPLOYEE, field)
        && this.$$crudAccessManager.canUpdateAllProvided(CrudEntityTypes.EMPLOYEE, field);
      return [{
        key: 'assigned',
        label: 'Zugeteilte Lehrkräfte',
        icon: 'mdi-human-male-board',
        empty: 'Keine Lehrkraft zugeteilt',
        editTitle: 'Zuteilungen bearbeiten',
        institutionKey: 'assignedTeachers',
        items: this.assigned,
        canEdit: canUpdate(EmployeeFields.ASSIGNED_INSTITUTIONS),
        open: () => (this.showAssign = true),
      }, {
        key: 'planned',
        label: 'Vorgemerkte Lehrkräfte',
        icon: 'mdi-account-clock-outline',
        empty: 'Keine Lehrkraft vorgemerkt',
        editTitle: 'Vormerkungen bearbeiten',
        institutionKey: 'plannedTeachers',
        items: this.planned,
        canEdit: canUpdate(EmployeeFields.PLANNED_INSTITUTIONS),
        open: () => (this.showPlan = true),
      }];
    },
  },
  methods: {
    name(item: Person) {
      return item.firstName || item.lastName ? `${item.firstName || ''} ${item.lastName || ''}`.trim() : 'Name unbekannt';
    },
    initials(item: Person) {
      return `${(item.firstName || '?')[0]}${(item.lastName || '')[0] || ''}`.toUpperCase();
    },
    show(id: mongoose.Types.ObjectId) {
      this.activeId = id;
      this.showCard = true;
    },
    async load() {
      if (!this.canRead) {
        return;
      }
      this.loading = true;
      try {
        const find = (filter: Record<string, unknown>) => EmployeeHandlerAPI.find({
          skipPagination: true,
          filter: {isActive: true, ...filter},
          fields: ['firstName', 'lastName'],
        } as any);
        const [assigned, planned] = await Promise.all([
          find({assignedInstitutions: [this.institutionId]}),
          find({plannedInstitutions: [this.institutionId]}),
        ]);
        this.assigned = assigned.employees;
        this.planned = planned.employees;
      } catch (e) {
        this.$$showSnackbar('Die Lehrkräfte des Standorts konnten nicht geladen werden', 'error', e);
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    institutionId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  mounted() {
    EventBus.$on('update:institution', this.load);
  },
  beforeDestroy() {
    EventBus.$off('update:institution', this.load);
  },
});
</script>

<style scoped>
.mini-avatar {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  display: inline-grid;
  place-items: center;
  font-size: 9px;
  font-weight: 700;
  background: rgba(21, 101, 192, .12);
  color: #1565C0;
}
</style>
<style src="./itemDetails.css"></style>
<style src="@/components/common/modern/modern.css"></style>
